import isNumber from "lodash/isNumber";
import isNaN from "lodash/isNaN";
import isNull from "lodash/isNull";
import { isUndefined } from "lodash";
import { MISSING_VALUE, NOT_AVAILABLE_VALUE } from "../components/Constants";
import { BuildingPanelField } from "../ts-types/mapsTypes";
import { SECTION_LENGTH_THRESHOLDS } from "../constants/maps-constants";
import { CRIME_DESCRIPTIONS, CRIME_THRESHOLDS } from "./constants";

export const getHhCrimeScoreWithCategory = (response: any) => {
  if (!response) return undefined;

  const score = response?.crime_score_index;
  const category = response?.crime_score;

  if (
    score === null ||
    score === undefined ||
    score === "" ||
    category === null ||
    category === undefined ||
    category === ""
  ) {
    return undefined;
  }

  return `${score} (${category})`;
};

export const constructHeaderName = (
  firstName?: string,
  email?: string,
): string => {
  if (firstName && firstName.length > 0) {
    return firstName[0].toUpperCase();
  }
  if (email && email.length > 0) {
    return email[0].toUpperCase();
  }
  return "U";
};

export const formatCurrency = (
  c: any,
  abbr?: boolean,
  decimals: number = 1,
) => {
  let value = c;
  if (!isNumber(c)) {
    value = parseFloat((c || "").toString());
    value = Math.fround(value);
  }
  if (c === 0) {
    return "$0";
  }

  if (isNaN(value)) {
    return "";
  }

  // Check if the `abbr` parameter is set to `true`
  if (abbr) {
    // Define an array of abbreviations to use for large numbers
    const abbreviations = ["", "K", "M", "B", "T"];

    // Determine the appropriate abbreviation to use based on the number's magnitude
    const magnitude = Math.floor(Math.log10(Math.abs(value)) / 3);
    const abbreviation = abbreviations[magnitude];

    // Divide the number by the appropriate power of 10 and round to 1 decimal place
    value = value / Math.pow(10, magnitude * 3);
    value = Math.round(value * 10) / 10;

    if (decimals === 0) {
      value = Math.round(value);
    } else {
      const multiplier = Math.pow(10, decimals);
      value = Math.round(value * multiplier) / multiplier;
    }

    const formattedValue =
      decimals === 0 ? value.toString() : value.toFixed(decimals);

    // Append the abbreviation to the formatted string
    return value > 0
      ? "$" + formattedValue + abbreviation
      : "-$" +
          Math.abs(value).toFixed(decimals === 0 ? 0 : decimals) +
          abbreviation;
  } else {
    // Use the default currency formatting
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  }
};

export const formatPrecisionCurrency = (c: any, abbr?: boolean) => {
  let value = c;

  if (!isNumber(c)) {
    value = parseFloat((c || "").toString());
    value = Math.fround(value);
  }

  if (c === 0) {
    return "$0";
  }

  if (isNaN(value)) {
    return "";
  }

  if (abbr) {
    const absValue = Math.abs(value);
    const isNegative = value < 0;

    if (absValue < 1000) {
      return `${isNegative ? "-" : ""}$${Math.round(absValue)}`;
    }

    const abbreviations = ["", "K", "M", "B", "T"];
    const magnitude = Math.floor(Math.log10(absValue) / 3);
    const abbreviation = abbreviations[magnitude] || "T+";

    const scaledValue = absValue / Math.pow(1000, magnitude);

    const formattedValue = scaledValue.toFixed(1);

    return `${isNegative ? "-" : ""}$${formattedValue}${abbreviation}`;
  }

  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

export const formatNumber = (
  n: string | number,
  abbr?: boolean,
  decimals: number = 1,
) => {
  // normalize
  let value = Number(n);

  // validate
  if (!Number.isFinite(value)) return "";

  if (value === 0) {
    return "0";
  }

  // Check if the `abbr` parameter is set to `true`
  if (abbr) {
    // Define an array of abbreviations to use for large numbers
    const abbreviations = ["", "K", "M", "B", "T"];

    // Determine the appropriate abbreviation to use based on the number's magnitude
    const magnitude = Math.floor(Math.log10(Math.abs(value)) / 3);
    const abbreviation = abbreviations[magnitude];

    // Divide the number by the appropriate power of 10 and round to specified decimal places
    value = value / Math.pow(10, magnitude * 3);

    if (decimals === 0) {
      value = Math.round(value);
    } else {
      const multiplier = Math.pow(10, decimals);
      value = Math.round(value * multiplier) / multiplier;
    }

    const formattedValue =
      decimals === 0 ? value.toString() : value.toFixed(decimals);

    // Append the abbreviation to the formatted string
    return value > 0
      ? formattedValue + abbreviation
      : "-" +
          Math.abs(value).toFixed(decimals === 0 ? 0 : decimals) +
          abbreviation;
  } else {
    // Use the default number formatting with commas
    return value.toLocaleString("en-US", {
      maximumFractionDigits: 0,
    });
  }
};

export const formatWithCommas = (
  value: string | number | undefined,
  roundToZero = true,
  preserveDecimalsUnderOne = false,
): string => {
  if (isNaN(value) || isUndefined(value) || isNull(value)) {
    return "";
  }

  if (!value) {
    return `${value}`;
  }
  if (typeof value === "string") {
    value = parseFloat(value);

    if (isNaN(value)) {
      return "";
    }
  }
  const formatter = new Intl.NumberFormat("en-US");

  if (preserveDecimalsUnderOne && value < 1) {
    return formatter.format(value);
  }

  const formattedValue = roundToZero ? value?.toFixed(0) : value?.toFixed(2);
  return formatter.format(parseFloat(formattedValue));
};

export const parseCurrency = (c: string, removeDollarSign = false) => {
  if (c === "") {
    return 0;
  }

  let value = c;

  // Remove commas
  value = value.replace(/,/g, "");

  // Remove dollar signs if specified
  if (removeDollarSign) {
    value = value.replace(/\$/g, "");
  }

  return parseFloat(value);
};

export const formatPercentage = (value: number, decimalPlaces: number = 2) => {
  if (isNaN(value) || isUndefined(value) || isNull(value)) {
    return "";
  }

  return `${value.toFixed(decimalPlaces)}%`;
};

export const formatDistance = (value: any, total?: number) => {
  const distance = (value / 10) * (total ?? 1);
  if (distance >= 5280) {
    return `${(distance / 5280).toFixed(2)} mi`;
  }
  return `${Math.round(distance)} ft`;
};

export const formatDistanceWithUnit = (
  valueInFeet: number | string | undefined,
) => {
  if (
    valueInFeet === undefined ||
    valueInFeet === null ||
    isNaN(Number(valueInFeet))
  ) {
    return "";
  }

  if (typeof valueInFeet === "string") {
    valueInFeet = parseFloat(valueInFeet);
  }

  if (valueInFeet < 5280) {
    return `${formatWithCommas(Math.round(valueInFeet))} ft`;
  } else {
    const miles = (valueInFeet / 5280).toFixed(2);
    return `${formatWithCommas(miles)} mi`;
  }
};

export const formatDistanceWithCommas = (
  value: string | number | undefined,
  roundToZero = true,
  preserveDecimalsUnderOne = false,
): string => {
  if (value === undefined || value === null || isNaN(Number(value))) {
    return "";
  }

  if (!value) {
    return `${value}`;
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  const formatter = new Intl.NumberFormat("en-US");

  if (preserveDecimalsUnderOne && numValue < 1) {
    return formatter.format(numValue);
  }

  const formattedValue = roundToZero
    ? numValue.toFixed(0)
    : numValue.toFixed(2);

  return formatter.format(parseFloat(formattedValue));
};

export const formatEasiScore = (value: number) => {
  if (isNaN(value) || isUndefined(value) || isNull(value)) {
    return "";
  }

  for (let i = CRIME_THRESHOLDS.length - 1; i >= 0; i--) {
    if (value > CRIME_THRESHOLDS[i]) return CRIME_DESCRIPTIONS[i];
  }
  return CRIME_DESCRIPTIONS[0];
};

export const formatEasiLetterGrade = (value: string) => {
  if (isUndefined(value) || isNull(value)) {
    return "";
  }

  if (value === "A") {
    return "A - Minimal Risk";
  } else if (value === "B") {
    return "B - Low Risk";
  } else if (value === "C") {
    return "C - Moderate Risk";
  } else if (value === "D") {
    return "D - Elevated Risk";
  } else if (value === "F") {
    return "F - High Risk";
  } else {
    return value;
  }
};

export const formatUnitWithCommas = (value: string | number, unit: string) => {
  if (isNaN(value) || isUndefined(value) || isNull(value) || value === "") {
    return "";
  }

  if (value === 0) {
    return `0 ${unit}`;
  }

  return `${formatWithCommas(value, false)} ${unit}`;
};

export const formatUnit = (value: string | number, unit: string) => {
  if (isNaN(value) || isUndefined(value) || isNull(value) || value === "") {
    return "";
  }

  if (value === 0) {
    return `0 ${unit}`;
  }

  return `${value} ${unit}`;
};

export const formatWithCapitalize = (value: string | undefined): string => {
  if (isUndefined(value) || isNull(value)) {
    return "";
  }

  const formattedValue = `${value}`;

  return formattedValue.charAt(0).toUpperCase() + formattedValue.slice(1);
};

export const formatWithCommasAndDegrees = (
  value: string | number | undefined,
): string => {
  if (value === undefined || value === null) {
    return "";
  }

  // Normalize to string first
  const str = String(value);
  const num = Number(str.replace(/,/g, ""));

  if (isNaN(num)) {
    return `${str}°`; // fallback
  }

  return `${num.toLocaleString("en-US")}°`;
};

export const formatCoordinate = (value: string | number | undefined | null) => {
  if (value === undefined || value === null) return null;

  const numValue = typeof value === "string" ? parseFloat(value) : value;

  return !isNaN(numValue) ? numValue.toFixed(5) : null;
};

/*
 * This function formats a value based on a list of choices and a formatter function.
 * It first checks for a valid value in the list of choices, and if found, applies the formatter.
 * If no valid value is found, it checks if any integration was called and returns a corresponding message.
 *
 * @param {Array<T | undefined | null>} valueChoices - An array of possible values to format.
 * @param {Array<any | undefined | null>} responseChoices - An array of responses from integrations.
 * @param {(value: T, ...args: R[]) => string} [formatter] - A function to format the valid value.
 * @param {...R[]} formatterArgs - Additional arguments to pass to the formatter function.
 * @returns {string} The formatted string or a fallback message.
 */
export const formatWithFallback = <T, R>(
  valueChoices: Array<T | undefined | null>,
  responseChoices: Array<any | undefined | null>,
  formatter: (value: T, ...args: R[]) => string = (v: T) => String(v),
  ...formatterArgs: R[]
): string => {
  const validValue = valueChoices.find(
    (value) =>
      !(
        isNull(value) ||
        isUndefined(value) ||
        value === "" ||
        (typeof value === "number" && isNaN(value)) ||
        (typeof value === "object" &&
          value !== null &&
          Object.keys(value).length === 0)
      ),
  );

  const wasIntegrationCalled = responseChoices.some(
    (value) =>
      !isUndefined(value) &&
      !(
        typeof value === "object" &&
        value !== null &&
        Object.keys(value).length === 0
      ),
  );

  if (validValue !== undefined) {
    return formatter(validValue as T, ...formatterArgs);
  }

  if (wasIntegrationCalled) {
    return NOT_AVAILABLE_VALUE;
  } else {
    return MISSING_VALUE;
  }
};

export const getFloodColor = (value: string): string => {
  if (!value) return "DEFAULT";

  const zones = [
    "A",
    "AO",
    "AE",
    "A99",
    "AH",
    "AR",
    "AR/A",
    "AR/AE",
    "AR/AH",
    "AR/AO",
    "AR/A1-30",
  ];

  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/^ZONE\s+/, "");

  // Explicit match
  if (zones.includes(normalized)) return "CRITICAL";

  // Handle ranges like A1–A30
  if (/^A\d{1,2}$/.test(normalized)) {
    const n = Number(normalized.slice(1));
    if (n >= 1 && n <= 30) return "CRITICAL";
  }

  // Handle ranges like V1–V30
  if (/^V\d{1,2}$/.test(normalized)) {
    const n = Number(normalized.slice(1));
    if (n >= 1 && n <= 30) return "CRITICAL";
  }

  // Single "V"
  if (normalized === "V") return "CRITICAL";

  return "DEFAULT";
};

export const getStormColor = (value: string): string => {
  if (value != "N/A" && value != "-") {
    return "CRITICAL";
  }
  return "DEFAULT";
};

export const getCrimeColor = (value: string): string => {
  if (value[0] == "D" || value[0] == "F") {
    return "CRITICAL";
  }
  return "DEFAULT";
};

export const getFireProtectionColor = (value: string | number): string => {
  if (value === null || value === undefined) return "DEFAULT";

  // If value is a number already
  const raw = typeof value === "number" ? value.toString() : value;

  // Strip "P" if present, then parse
  const num = parseInt(raw.replace(/^P/i, ""), 10);

  if (!isNaN(num) && num > 5) {
    return "CRITICAL";
  }

  return "DEFAULT";
};

export const getDtcColor = (value: string): string => {
  if (!value) return "DEFAULT";

  // Extract number before "mi" (handles "30.45 mi", "8 mi", etc.)
  const match = value.match(/([\d.]+)\s*mi/i);

  if (match) {
    const miles = parseFloat(match[1]);
    if (!isNaN(miles) && miles < 10) {
      return "CRITICAL";
    }
  }

  return "DEFAULT";
};

export const formatSectionTooltip = (
  section: BuildingPanelField,
  isLongSection: boolean = false,
): BuildingPanelField => {
  if (
    section.v &&
    section.v.toString().length <
      SECTION_LENGTH_THRESHOLDS[isLongSection ? "LONG" : "SHORT"]
  ) {
    return section;
  }

  const textWithEllipsis =
    section.v
      .toString()
      .slice(
        0,
        SECTION_LENGTH_THRESHOLDS[isLongSection ? "LONG" : "SHORT"] - 3,
      ) + "...";

  return {
    ...section,
    v: textWithEllipsis,
    vt: section.v,
  };
};

export const formatX100 = (
  value: any,
  decimalPlaces: number = 0,
  withPercent: boolean = false,
) => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (isNaN(num)) return "";
  const result = (num * 100).toFixed(decimalPlaces);
  return withPercent ? `${result}%` : result;
};

export const getNumber = (value: any): number => {
  if (typeof value === "number") {
    return value;
  } else if (typeof value === "string" && !isNaN(Number(value))) {
    return Number(value);
  }
  return 0;
};

/**
 * Formats a TIV (total insured value) as an abbreviated dollar amount.
 *
 * For example:
 *
 * ```typescript
 * formatTIV(2500000); // "$2.5M"
 * formatTIV("1000000"); // "$1M"
 * formatTIV(1500000000); // "$1.5B"
 * formatTIV(500); // "$500"
 * ```
 */
export const formatTIV = (value: number | string): string => {
  const num = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(num)) return "";

  const abbreviate = (scaled: number): string => {
    const fixed = scaled.toFixed(1);
    return fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed;
  };

  if (num >= 1000000000) return `$${abbreviate(num / 1000000000)}B`;
  if (num >= 1000000) return `$${abbreviate(num / 1000000)}M`;
  if (num >= 1000) return `$${abbreviate(num / 1000)}K`;
  return `$${num.toFixed(0)}`;
};

/**
 * Formats an area (e.g. square footage) as an abbreviated number.
 *
 * For example:
 *
 * ```typescript
 * formatArea(1200000); // "1.2M"
 * formatArea(2500); // "2.5K"
 * formatArea(653.7); // "654"
 * ```
 */
export const formatArea = (area: number): string => {
  if (!Number.isFinite(area)) return "";
  if (area >= 1000000) return `${(area / 1000000).toFixed(1)}M`;
  if (area >= 1000) return `${(area / 1000).toFixed(1)}K`;
  return `${Math.round(area)}`;
};
